<template>
  <v-card>
    <v-toolbar
      class="settings-window-toolbar"
      flat
      height="80"
    >
      <v-avatar
        :color="$t('Settings.Notification.Toolbar.Color')"
      >
        <v-icon dark>
          {{ $t('Settings.Notification.Toolbar.Icon') }}
        </v-icon>
      </v-avatar>

      <v-toolbar-title class="pl-3">
        <div class="body-3">
          {{ $t('Settings.Notification.Toolbar.Title') }}
        </div>

        <div class="text-caption">
          {{ $t('Settings.Notification.Toolbar.Subtitle') }}
        </div>
      </v-toolbar-title>

      <v-spacer />
    </v-toolbar>

    <v-divider />

    <v-card-text
      id="settingsWindow"
      style="overflow-y: auto;"
    >
      <v-form
        ref="dataForm"
        v-model="dataFormValid"
        class="fill-height"
        @submit.prevent
      >
        <vuetify-audio
          :disabled="!userCanManage"
          :show-progress-bar="false"
          :show-progress-timer="false"
          :sound.sync="selectedSound"
          :sounds="sounds"
        />

        <v-card flat>
          <v-card-title :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-4']">
            <v-icon class="pr-3">
              {{ $t('Settings.Notification.Toolbar.Icon') }}
            </v-icon>

            <h5 class="text-body-2 font-weight-bold">
              {{ $t('Settings.Notification.Toolbar.Title') }}
            </h5>
          </v-card-title>

          <v-divider />

          <template v-if="viewData">
            <v-card-text>
              <v-container
                class="pa-0"
                fluid
              >
                <!-- Store Email -->
                <v-row dense>
                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.StoreEmail.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption mr-1">{{ viewData.Store.Email }}</span>

                    <v-btn
                      :to="{name: 'SystemSettingsRestaurant'}"
                      color="cyan"
                      dark
                      icon
                      x-small
                    >
                      <v-icon small>
                        mdi-email-edit-outline
                      </v-icon>
                    </v-btn>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Store SMS -->
                <v-row dense>
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.StoreSms.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <div class="mt-1">
                      <table class="text-caption">
                        <tr v-if="appNotifySmsSender">
                          <td><span class="font-weight-bold">{{ $t('Settings.Notification.Data.StoreSms.Sender') }}:</span></td>
                          <td>
                            <v-chip
                              class="ml-2 px-2 font-weight-bold justify-center"
                              color="info"
                              label
                              style="min-width: 100px"
                              x-small
                            >
                              {{ appNotifySmsSender }}
                            </v-chip>
                          </td>
                        </tr>

                        <tr>
                          <td><span class="font-weight-bold">{{ $t('Settings.Notification.Data.StoreSms.Credits') }}:</span></td>
                          <td>
                            <v-chip
                              :color="appNotifySmsColor"
                              class="ml-2 px-2 font-weight-bold justify-center"
                              dark
                              label
                              style="min-width: 100px"
                              x-small
                            >
                              {{ appNotifySmsCredits }}
                            </v-chip>
                          </td>
                        </tr>
                      </table>
                    </div>
                  </v-col>

                  <v-col class="shrink align-content-center">
                    <v-btn
                      v-if="!appNotifySmsActive"
                      color="primary"
                      @click="onRequestDialogShow('Sms')"
                    >
                      {{ $t('Settings.Notification.SupportRequest.Contact') }}
                    </v-btn>

                    <template v-else>
                      <v-icon color="green">
                        mdi-cellphone-message
                      </v-icon>
                    </template>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Registration Notifications -->
                <v-row
                  v-if="posUserHasComponentPermission('Online', 'ACCESS')"
                  dense
                >
                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Registration.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Registration.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col cols="12">
                    <v-btn-toggle
                      v-model="viewData.Registration"
                      :disabled="!userCanManage"
                      color="success"
                      dense
                      multiple
                    >
                      <v-btn
                        :disabled="!userCanManage"
                        text
                        value="customer"
                      >
                        {{ $t('Settings.Notification.Data.Button.To.Customer').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :disabled="!userCanManage"
                        text
                        value="location"
                      >
                        {{ $t('Settings.Notification.Data.Button.To.Store').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Notification Emails -->
                <v-row dense>
                  <v-col cols="6">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Emails.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Emails.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col
                    class="text-right"
                    cols="6"
                  >
                    <div class="d-inline-block">
                      <v-switch
                        v-model="viewData.CanAddCustomNotificationEmail"
                        :disabled="!userCanManage"
                        :label="viewData.CanAddCustomNotificationEmail ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                        class="ma-0 pa-0"
                        color="success"
                        hide-details
                        inset
                      >
                        <template #prepend>
                          <h5 class="pt-2 mr-2 grey--text text--darken-1 text-no-wrap">
                            {{ $t('Settings.Notification.Data.Emails.CanAddCustomEmail') }}
                          </h5>
                        </template>
                      </v-switch>
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <v-card
                      :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                      flat
                    >
                      <v-card-text class="pa-2">
                        <v-simple-table>
                          <template #default>
                            <thead>
                              <tr>
                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Emails.Header.Email') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Emails.Header.Name') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Emails.Header.Notes') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Emails.Header.Type') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Emails.Header.Active') }}
                                </th>

                                <th class="text-right pa-1">
                                  <v-btn
                                    class="primary white--text"
                                    depressed
                                    icon
                                    x-small
                                    @click="addNotifyEmail"
                                  >
                                    <v-icon>mdi-plus</v-icon>
                                  </v-btn>
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              <template v-for="(email, emailIdx) in viewData.Emails">
                                <tr :key="`notification-email-${emailIdx}`">
                                  <td class="pa-1 text-center">
                                    <v-text-field
                                      v-model="email.Email"
                                      :disabled="!userCanManage"
                                      :rules="validationRules.dataForm.Email"
                                      class="elevation-0 d-inline-block"
                                      dense
                                      hide-details="auto"
                                      outlined
                                      required
                                      single-line
                                      style="min-width: 200px; max-width: 350px;"
                                    />
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-text-field
                                      v-model="email.Label"
                                      :disabled="!userCanManage"
                                      class="elevation-0 d-inline-block"
                                      dense
                                      hide-details
                                      outlined
                                      required
                                      single-line
                                      style="min-width: 200px; max-width: 350px;"
                                    />
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-text-field
                                      v-model="email.Notes"
                                      :disabled="!userCanManage"
                                      class="elevation-0 d-inline-block"
                                      dense
                                      hide-details
                                      outlined
                                      required
                                      single-line
                                      style="min-width: 200px; max-width: 350px;"
                                    />
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="email.Type"
                                      :disabled="!userCanManage"
                                      :items="Object.values($t('Settings.Notification.Data.Emails.Type'))"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Title"
                                      item-value="Id"
                                      multiple
                                      outlined
                                      single-line
                                      style="min-width: 280px; max-width: 280px;"
                                      @change="changeNotifyEmailType(emailIdx)"
                                    >
                                      <template #selection="{item}">
                                        <div class="my-1">
                                          <v-chip
                                            class="pl-0"
                                            dark
                                            label
                                            small
                                            style="width: 100%;"
                                          >
                                            <v-avatar
                                              :color="item.Color"
                                              class="mr-2"
                                              tile
                                            >
                                              <v-icon class="white--text">
                                                {{ item.Icon }}
                                              </v-icon>
                                            </v-avatar>
                                            <span>{{ item.Title }}</span>
                                          </v-chip>
                                        </div>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar
                                          :color="data.item.Color"
                                          :size="28"
                                        >
                                          <v-icon class="white--text">
                                            {{ data.item.Icon }}
                                          </v-icon>
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Title }}</v-list-item-title>
                                          <v-list-item-subtitle>{{ data.item.Subtitle | truncate }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-switch
                                      v-model="email.Active"
                                      :disabled="!userCanManage"
                                      class="ma-0 pa-0 d-inline-flex"
                                      color="success"
                                      hide-details
                                      inset
                                    />
                                  </td>

                                  <td class="pa-1 text-right">
                                    <v-btn
                                      v-if="!email.Id"
                                      class="red white--text"
                                      depressed
                                      icon
                                      x-small
                                      @click="removeNotifyEmail(emailIdx)"
                                    >
                                      <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                  </td>
                                </tr>
                              </template>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Orders Delivery Notify -->
                <v-row dense>
                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Delivery.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Delivery.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col cols="12">
                    <v-card
                      :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                      flat
                    >
                      <v-card-text class="pa-2">
                        <v-simple-table>
                          <template #default>
                            <thead>
                              <tr>
                                <th class="text-center pa-1">
                                  {{ $t('Order.SourceType.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Order.FilterStatuses.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Order.Type.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Email') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Push') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Sms') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Common.Button.Toggle.ActiveF') }}
                                </th>

                                <th class="text-right pa-1">
                                  <v-btn
                                    class="primary white--text"
                                    depressed
                                    icon
                                    x-small
                                    @click="addDeliveryNotifySource"
                                  >
                                    <v-icon>mdi-plus</v-icon>
                                  </v-btn>
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              <template v-for="(sourceType, sourceTypeIdx) in viewData.DeliveryNotify">
                                <tr :key="`source-type-${sourceTypeIdx}`">
                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.source"
                                      :items="activeSourceTypesGroupsWithGroups"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.SourceTypes.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 300px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Avatar.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon
                                              v-if="item.Avatar.Icon"
                                              class="white--text"
                                            >
                                              {{ item.Avatar.Icon }}
                                            </v-icon>

                                            <img
                                              v-else
                                              :src="item.Avatar.Img"
                                            >
                                          </v-avatar>
                                          <span>{{ item.Description }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar
                                          :color="data.item.Avatar.Color"
                                          :size="28"
                                        >
                                          <v-icon
                                            v-if="data.item.Avatar.Icon"
                                            class="white--text"
                                          >
                                            {{ data.item.Avatar.Icon }}
                                          </v-icon>

                                          <img
                                            v-else
                                            :src="data.item.Avatar.Img"
                                          >
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                          <v-list-item-subtitle>{{ data.item.Description | truncate }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.status"
                                      :items="orderStatusesFiltered"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.OrderStatus.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 300px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Avatar.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon class="white--text">
                                              {{ item.Avatar.Icon }}
                                            </v-icon>
                                          </v-avatar>
                                          <span>{{ item.Name }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar :color="data.item.Avatar.Color">
                                          <v-icon dark>
                                            {{ data.item.Avatar.Icon }}
                                          </v-icon>
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.type"
                                      :items="orderTypesListFiltered"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.OrderType.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 250px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon class="white--text">
                                              {{ item.Icon }}
                                            </v-icon>
                                          </v-avatar>
                                          <span>{{ item.Name }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar :color="data.item.Color">
                                          <v-icon dark>
                                            {{ data.item.Icon }}
                                          </v-icon>
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn-toggle
                                      v-model="sourceType.via.email"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.email.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                    <br>
                                    <notification-editable-lang-value
                                      :index="sourceTypeIdx"
                                      :value="sourceType.message.email"
                                      type="email"
                                      @save="onDeliveryNotificationMessageSave"
                                    />
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn
                                      v-if="!appNotifyPushActive"
                                      color="primary"
                                      @click="onRequestDialogShow('Push')"
                                    >
                                      {{ $t('Settings.Notification.SupportRequest.Contact') }}
                                    </v-btn>

                                    <v-btn-toggle
                                      v-else
                                      v-model="sourceType.via.push"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.push.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                    <br>
                                    <notification-editable-lang-value
                                      :index="sourceTypeIdx"
                                      :value="sourceType.message.push"
                                      type="push"
                                      @save="onDeliveryNotificationMessageSave"
                                    />
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn
                                      v-if="!appNotifySmsActive"
                                      color="primary"
                                      @click="onRequestDialogShow('Sms')"
                                    >
                                      {{ $t('Settings.Notification.SupportRequest.Contact') }}
                                    </v-btn>

                                    <v-btn-toggle
                                      v-else
                                      v-model="sourceType.via.sms"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.sms.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                    <br>
                                    <notification-editable-lang-value
                                      :index="sourceTypeIdx"
                                      :value="sourceType.message.sms"
                                      type="sms"
                                      @save="onDeliveryNotificationMessageSave"
                                    />
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-switch
                                      v-model="sourceType.active"
                                      :disabled="!userCanManage"
                                      class="ma-0 pa-0 d-inline-flex"
                                      color="success"
                                      hide-details
                                      inset
                                    />
                                  </td>

                                  <td class="pa-1 text-right">
                                    <v-btn
                                      class="red white--text"
                                      depressed
                                      icon
                                      x-small
                                      @click="removeDeliveryNotifySource(sourceTypeIdx)"
                                    >
                                      <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                  </td>
                                </tr>
                              </template>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Orders Automatic Notify -->
                <v-row dense>
                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Order.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Order.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col cols="12">
                    <v-card
                      :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                      flat
                    >
                      <v-card-text class="pa-2">
                        <v-simple-table>
                          <template #default>
                            <thead>
                              <tr>
                                <th class="text-center pa-1">
                                  {{ $t('Order.SourceType.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Order.FilterStatuses.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Order.Type.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Email') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Push') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Sms') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Common.Button.Toggle.ActiveF') }}
                                </th>

                                <th class="text-right pa-1">
                                  <v-btn
                                    class="primary white--text"
                                    depressed
                                    icon
                                    x-small
                                    @click="addNotifySource"
                                  >
                                    <v-icon>mdi-plus</v-icon>
                                  </v-btn>
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              <template v-for="(sourceType, sourceTypeIdx) in viewData.Notify">
                                <tr :key="`source-type-${sourceTypeIdx}`">
                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.source"
                                      :items="activeSourceTypesGroupsWithGroups"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.SourceTypes.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 300px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Avatar.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon
                                              v-if="item.Avatar.Icon"
                                              class="white--text"
                                            >
                                              {{ item.Avatar.Icon }}
                                            </v-icon>

                                            <img
                                              v-else
                                              :src="item.Avatar.Img"
                                            >
                                          </v-avatar>
                                          <span>{{ item.Description }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar
                                          :color="data.item.Avatar.Color"
                                          :size="28"
                                        >
                                          <v-icon
                                            v-if="data.item.Avatar.Icon"
                                            class="white--text"
                                          >
                                            {{ data.item.Avatar.Icon }}
                                          </v-icon>

                                          <img
                                            v-else
                                            :src="data.item.Avatar.Img"
                                          >
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                          <v-list-item-subtitle>{{ data.item.Description | truncate }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.status"
                                      :items="orderStatusesFiltered"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.OrderStatus.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 300px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Avatar.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon class="white--text">
                                              {{ item.Avatar.Icon }}
                                            </v-icon>
                                          </v-avatar>
                                          <span>{{ item.Name }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar :color="data.item.Avatar.Color">
                                          <v-icon dark>
                                            {{ data.item.Avatar.Icon }}
                                          </v-icon>
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.type"
                                      :items="orderTypesListFiltered"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.OrderType.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 250px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon class="white--text">
                                              {{ item.Icon }}
                                            </v-icon>
                                          </v-avatar>
                                          <span>{{ item.Name }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar :color="data.item.Color">
                                          <v-icon dark>
                                            {{ data.item.Icon }}
                                          </v-icon>
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn-toggle
                                      v-model="sourceType.via.email"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.email.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn
                                      v-if="!appNotifyPushActive"
                                      color="primary"
                                      @click="onRequestDialogShow('Push')"
                                    >
                                      {{ $t('Settings.Notification.SupportRequest.Contact') }}
                                    </v-btn>

                                    <v-btn-toggle
                                      v-else
                                      v-model="sourceType.via.push"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.push.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn
                                      v-if="!appNotifySmsActive"
                                      color="primary"
                                      @click="onRequestDialogShow('Sms')"
                                    >
                                      {{ $t('Settings.Notification.SupportRequest.Contact') }}
                                    </v-btn>

                                    <v-btn-toggle
                                      v-else
                                      v-model="sourceType.via.sms"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.sms.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-switch
                                      v-model="sourceType.active"
                                      :disabled="!userCanManage"
                                      class="ma-0 pa-0 d-inline-flex"
                                      color="success"
                                      hide-details
                                      inset
                                    />
                                  </td>

                                  <td class="pa-1 text-right">
                                    <v-btn
                                      class="red white--text"
                                      depressed
                                      icon
                                      x-small
                                      @click="removeNotifySource(sourceTypeIdx)"
                                    >
                                      <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                  </td>
                                </tr>
                              </template>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Order Manual Notify -->
                <v-row dense>
                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Order.TitleManual')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Order.SubtitleManual')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col cols="12">
                    <v-card
                      :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                      flat
                    >
                      <v-card-text class="pa-2">
                        <v-simple-table>
                          <template #default>
                            <thead>
                              <tr>
                                <th class="text-center pa-1">
                                  {{ $t('Order.SourceType.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Order.FilterStatuses.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Order.Type.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Email') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Push') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Settings.Notification.Data.Button.Channel.Sms') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Common.Button.Toggle.ActiveF') }}
                                </th>

                                <th class="text-right pa-1">
                                  <v-btn
                                    class="primary white--text"
                                    depressed
                                    icon
                                    x-small
                                    @click="addNotifyManualSource"
                                  >
                                    <v-icon>mdi-plus</v-icon>
                                  </v-btn>
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              <template v-for="(sourceType, sourceTypeIdx) in viewData.NotifyManual">
                                <tr :key="`source-type-${sourceTypeIdx}`">
                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.source"
                                      :items="activeSourceTypesGroupsWithGroups"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.SourceTypes.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 300px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Avatar.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon
                                              v-if="item.Avatar.Icon"
                                              class="white--text"
                                            >
                                              {{ item.Avatar.Icon }}
                                            </v-icon>

                                            <img
                                              v-else
                                              :src="item.Avatar.Img"
                                            >
                                          </v-avatar>
                                          <span>{{ item.Description }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar
                                          :color="data.item.Avatar.Color"
                                          :size="28"
                                        >
                                          <v-icon
                                            v-if="data.item.Avatar.Icon"
                                            class="white--text"
                                          >
                                            {{ data.item.Avatar.Icon }}
                                          </v-icon>

                                          <img
                                            v-else
                                            :src="data.item.Avatar.Img"
                                          >
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                          <v-list-item-subtitle>{{ data.item.Description | truncate }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.status"
                                      :items="orderStatusesFiltered"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.OrderStatus.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 300px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Avatar.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon class="white--text">
                                              {{ item.Avatar.Icon }}
                                            </v-icon>
                                          </v-avatar>
                                          <span>{{ item.Name }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar :color="data.item.Avatar.Color">
                                          <v-icon dark>
                                            {{ data.item.Avatar.Icon }}
                                          </v-icon>
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.type"
                                      :items="orderTypesListFiltered"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.OrderType.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 250px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon class="white--text">
                                              {{ item.Icon }}
                                            </v-icon>
                                          </v-avatar>
                                          <span>{{ item.Name }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar :color="data.item.Color">
                                          <v-icon dark>
                                            {{ data.item.Icon }}
                                          </v-icon>
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn-toggle
                                      v-model="sourceType.via.email"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.email.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn
                                      v-if="!appNotifyPushActive"
                                      color="primary"
                                      @click="onRequestDialogShow('Push')"
                                    >
                                      {{ $t('Settings.Notification.SupportRequest.Contact') }}
                                    </v-btn>

                                    <v-btn-toggle
                                      v-else
                                      v-model="sourceType.via.push"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.push.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-btn
                                      v-if="!appNotifySmsActive"
                                      color="primary"
                                      @click="onRequestDialogShow('Sms')"
                                    >
                                      {{ $t('Settings.Notification.SupportRequest.Contact') }}
                                    </v-btn>

                                    <v-btn-toggle
                                      v-else
                                      v-model="sourceType.via.sms"
                                      :disabled="!userCanManage"
                                      class=""
                                      color="success"
                                      dense
                                      multiple
                                    >
                                      <v-btn
                                        :disabled="!userCanManage"
                                        text
                                        value="customer"
                                      >
                                        {{
                                          sourceType.via.sms.includes('customer') ? $t('Common.Button.Toggle.Active').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Toggle.Inactive').toLocaleUpperCase($i18n.locale)
                                        }}
                                      </v-btn>
                                    </v-btn-toggle>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-switch
                                      v-model="sourceType.active"
                                      :disabled="!userCanManage"
                                      class="ma-0 pa-0 d-inline-flex"
                                      color="success"
                                      hide-details
                                      inset
                                    />
                                  </td>

                                  <td class="pa-1 text-right">
                                    <v-btn
                                      class="red white--text"
                                      depressed
                                      icon
                                      x-small
                                      @click="removeNotifyManualSource(sourceTypeIdx)"
                                    >
                                      <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                  </td>
                                </tr>
                              </template>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Order Status Actions Internal Notifications -->
                <v-row dense>
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.InternalOrder.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.InternalOrder.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col cols="auto">
                    <div class="d-inline-block">
                      <v-switch
                        v-model="viewData.OrderNotify.active"
                        :disabled="!userCanManage"
                        :label="viewData.OrderNotify.active ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                        class="ma-0 pa-0"
                        color="success"
                        hide-details
                        inset
                      />
                    </div>
                  </v-col>

                  <v-col
                    v-if="viewData.OrderNotify.active"
                    cols="12"
                  >
                    <v-container
                      class="px-0 pb-0"
                      fluid
                    >
                      <v-row
                        class="align-center pb-8 pb-sm-4 pb-md-1"
                        dense
                      >
                        <v-col cols="12">
                          <span
                            v-safe-html="$t('Settings.Notification.Data.InternalOrder.Snooze.Label', {minutes: getNotificationMinutes(viewData.OrderNotify.snoozeForMins)})"
                            class="text-caption"
                          />
                        </v-col>

                        <v-col cols="auto">
                          <v-chip
                            class="blue lighten-1 white--text elevation-0 pl-0 mr-3"
                            label
                            style="min-width: 160px;"
                          >
                            <v-avatar
                              class="blue darken-1 mr-2"
                              style="width: 32px !important; height: 32px !important;"
                              tile
                            >
                              <v-icon color="blue lighten-4">
                                mdi-alarm-snooze
                              </v-icon>
                            </v-avatar>

                            <span>{{ $t('Settings.Notification.Data.InternalOrder.Snooze.Title') }}</span>
                          </v-chip>
                        </v-col>

                        <v-col cols="auto">
                          <v-text-field
                            v-model.number="viewData.OrderNotify.snoozeForMins"
                            :prepend-inner-icon="$t('Settings.Notification.Data.InternalOrder.Snooze.Icon')"
                            :suffix="$t('Settings.Notification.Data.InternalOrder.Snooze.Suffix')"
                            dense
                            hide-details
                            max="120"
                            min="1"
                            outlined
                            single-line
                            step="1"
                            type="number"
                          />
                        </v-col>
                      </v-row>

                      <v-row
                        class="align-center pb-8 pb-sm-4 pb-md-1"
                        dense
                      >
                        <v-col cols="12">
                          <span
                            v-safe-html="$t('Settings.Notification.Data.InternalOrder.Accept.Label', {minutes: getNotificationMinutes(viewData.OrderNotify.notAcceptedMinsAfterReceived)})"
                            class="text-caption"
                          />
                        </v-col>

                        <v-col cols="auto">
                          <v-chip
                            :color="getOrderStatusById(OrderStatusEnum.Accepted).Avatar.Color"
                            class="pl-0 mr-3 white--text"
                            label
                            style="min-width: 160px;"
                          >
                            <v-avatar
                              :color="`${getOrderStatusById(OrderStatusEnum.Accepted).Avatar.Color} darken-3`"
                              style="height: 32px !important; width: 32px !important;"
                              tile
                            >
                              <v-icon>
                                {{ getOrderStatusById(OrderStatusEnum.Accepted).Avatar.Icon }}
                              </v-icon>
                            </v-avatar>
                            <span class="ml-2">{{ getOrderStatusById(OrderStatusEnum.Accepted).Name }}</span>
                          </v-chip>
                        </v-col>

                        <v-col cols="auto">
                          <v-text-field
                            v-model.number="viewData.OrderNotify.notAcceptedMinsAfterReceived"
                            :prepend-inner-icon="$t('Settings.Notification.Data.InternalOrder.Accept.Icon')"
                            :suffix="$t('Settings.Notification.Data.InternalOrder.Accept.Suffix')"
                            dense
                            hide-details
                            max="120"
                            min="1"
                            outlined
                            single-line
                            step="1"
                            type="number"
                          />
                        </v-col>
                      </v-row>

                      <v-row
                        class="align-center pb-8 pb-sm-4 pb-md-1"
                        dense
                      >
                        <v-col cols="12">
                          <span
                            v-safe-html="$t('Settings.Notification.Data.InternalOrder.CompletedDelivery.Label', {minutes: getNotificationMinutes(orderTypeDefaultTimes.delivery_time - viewData.OrderNotify.notCompletedMinsBeforeDeliveryTime), minutesBefore: getNotificationMinutes(viewData.OrderNotify.notCompletedMinsBeforeDeliveryTime)})"
                            class="text-caption"
                          />
                        </v-col>

                        <v-col cols="auto">
                          <v-chip
                            :color="getOrderStatusById(OrderStatusEnum.Completed).Avatar.Color"
                            class="pl-0 mr-3 white--text"
                            label
                            style="min-width: 160px;"
                          >
                            <v-avatar
                              :color="`${getOrderStatusById(OrderStatusEnum.Completed).Avatar.Color} darken-3`"
                              style="height: 32px !important; width: 32px !important;"
                              tile
                            >
                              <v-icon>
                                {{ getOrderStatusById(OrderStatusEnum.Completed).Avatar.Icon }}
                              </v-icon>
                            </v-avatar>
                            <span class="ml-2">{{ getOrderStatusById(OrderStatusEnum.Completed).Name }}</span>
                          </v-chip>
                        </v-col>

                        <v-col cols="auto">
                          <v-chip
                            class="purple lighten-1 white--text elevation-0 pl-0 mr-3"
                            label
                            style="min-width: 70px;"
                          >
                            <v-avatar
                              class="purple darken-1 mr-2"
                              style="width: 32px !important; height: 32px !important;"
                              tile
                            >
                              <v-icon color="purple lighten-4">
                                directions_bike
                              </v-icon>
                            </v-avatar>

                            <span>{{ convertMinutesToDisplayString(orderTypeDefaultTimes.delivery_time) }}</span>
                          </v-chip>
                        </v-col>

                        <v-col cols="auto">
                          <v-text-field
                            v-model.number="viewData.OrderNotify.notCompletedMinsBeforeDeliveryTime"
                            :max="orderTypeDefaultTimes.delivery_time"
                            :prepend-inner-icon="$t('Settings.Notification.Data.InternalOrder.CompletedDelivery.Icon')"
                            :suffix="$t('Settings.Notification.Data.InternalOrder.CompletedDelivery.Suffix')"
                            dense
                            hide-details
                            min="1"
                            outlined
                            single-line
                            step="1"
                            type="number"
                          />
                        </v-col>
                      </v-row>

                      <v-row
                        class="align-center pb-8 pb-sm-4 pb-md-1"
                        dense
                      >
                        <v-col cols="12">
                          <span
                            v-safe-html="$t('Settings.Notification.Data.InternalOrder.CompletedCollection.Label', {minutes: getNotificationMinutes(orderTypeDefaultTimes.collection_time - viewData.OrderNotify.notCompletedMinsBeforeCollectionTime), minutesBefore: getNotificationMinutes(viewData.OrderNotify.notCompletedMinsBeforeCollectionTime)})"
                            class="text-caption"
                          />
                        </v-col>

                        <v-col cols="auto">
                          <v-chip
                            :color="getOrderStatusById(OrderStatusEnum.Completed).Avatar.Color"
                            class="pl-0 mr-3 white--text"
                            label
                            style="min-width: 160px;"
                          >
                            <v-avatar
                              :color="`${getOrderStatusById(OrderStatusEnum.Completed).Avatar.Color} darken-3`"
                              style="height: 32px !important; width: 32px !important;"
                              tile
                            >
                              <v-icon>
                                {{ getOrderStatusById(OrderStatusEnum.Completed).Avatar.Icon }}
                              </v-icon>
                            </v-avatar>
                            <span class="ml-2">{{ getOrderStatusById(OrderStatusEnum.Completed).Name }}</span>
                          </v-chip>
                        </v-col>

                        <v-col cols="auto">
                          <v-chip
                            class="light-blue lighten-1 white--text elevation-0 pl-0 mr-3"
                            label
                            style="min-width: 70px;"
                          >
                            <v-avatar
                              class="light-blue darken-1 mr-2"
                              style="width: 32px !important; height: 32px !important;"
                              tile
                            >
                              <v-icon color="light-blue lighten-4">
                                directions_run
                              </v-icon>
                            </v-avatar>

                            <span>{{ convertMinutesToDisplayString(orderTypeDefaultTimes.collection_time) }}</span>
                          </v-chip>
                        </v-col>

                        <v-col cols="auto">
                          <v-text-field
                            v-model.number="viewData.OrderNotify.notCompletedMinsBeforeCollectionTime"
                            :max="orderTypeDefaultTimes.collection_time"
                            :prepend-inner-icon="$t('Settings.Notification.Data.InternalOrder.CompletedCollection.Icon')"
                            :suffix="$t('Settings.Notification.Data.InternalOrder.CompletedCollection.Suffix')"
                            dense
                            hide-details
                            min="1"
                            outlined
                            single-line
                            step="1"
                            type="number"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Reservations Notifications -->
                <v-row dense>
                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Reservation.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Notification.Data.Reservation.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col cols="12">
                    <v-container
                      class="px-0 pb-0"
                      fluid
                    >
                      <template v-for="(reservationStatus, reservationStatusIdx) in viewData.Reservation">
                        <v-row
                          :key="`reservation-status-${reservationStatus.id}-${reservationStatusIdx}`"
                          class="align-center pb-8 pb-sm-4 pb-md-1"
                          dense
                        >
                          <v-col cols="auto">
                            <v-chip
                              :color="getReservationStatusById(reservationStatus.id).Avatar.Color"
                              class="pl-0 mr-3 white--text"
                              label
                              style="min-width: 160px;"
                            >
                              <v-avatar
                                :color="`${getReservationStatusById(reservationStatus.id).Avatar.Color} darken-3`"
                                style="height: 32px !important; width: 32px !important;"
                                tile
                              >
                                <v-icon>
                                  {{ getReservationStatusById(reservationStatus.id).Avatar.Icon }}
                                </v-icon>
                              </v-avatar>
                              <span class="ml-2">{{ getReservationStatusById(reservationStatus.id).Name }}</span>
                            </v-chip>
                          </v-col>

                          <v-col cols="auto">
                            <v-btn-toggle
                              v-model="viewData.Reservation[reservationStatusIdx].to"
                              :disabled="!userCanManage"
                              class="mr-3"
                              color="success"
                              dense
                              multiple
                              @change="onReservationChange(viewData.Reservation[reservationStatusIdx])"
                            >
                              <v-btn
                                :disabled="!userCanManage"
                                text
                                value="customer"
                              >
                                {{ $t('Settings.Notification.Data.Button.To.Customer').toLocaleUpperCase($i18n.locale) }}
                              </v-btn>

                              <v-btn
                                :disabled="!userCanManage"
                                text
                                value="location"
                              >
                                {{ $t('Settings.Notification.Data.Button.To.Store').toLocaleUpperCase($i18n.locale) }}
                              </v-btn>
                            </v-btn-toggle>
                          </v-col>

                          <v-col cols="auto">
                            <v-btn-toggle
                              v-model="viewData.Reservation[reservationStatusIdx].channel"
                              :disabled="!userCanManage"
                              class="mr-3"
                              color="success"
                              dense
                              multiple
                              @change="onReservationChange(viewData.Reservation[reservationStatusIdx])"
                            >
                              <v-btn
                                :disabled="!userCanManage"
                                text
                                value="email"
                              >
                                {{ $t('Settings.Notification.Data.Button.Channel.Email').toLocaleUpperCase($i18n.locale) }}
                              </v-btn>

                              <v-btn
                                :disabled="!userCanManage || !appSupportsSmsNotifications"
                                text
                                value="sms"
                              >
                                {{ $t('Settings.Notification.Data.Button.Channel.Sms').toLocaleUpperCase($i18n.locale) }}
                              </v-btn>
                            </v-btn-toggle>
                          </v-col>

                          <v-col cols="auto">
                            <div class="d-inline-block">
                              <v-switch
                                v-model="viewData.Reservation[reservationStatusIdx].active"
                                :disabled="!userCanManage || isReservationActiveDisabled(viewData.Reservation[reservationStatusIdx])"
                                :label="reservationStatus.active ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                                class="ma-0 pa-0"
                                color="success"
                                hide-details
                                inset
                              />
                            </div>
                          </v-col>
                        </v-row>
                      </template>
                    </v-container>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </template>
        </v-card>
      </v-form>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4 settings-window-actions">
      <v-spacer />
      <v-btn
        class="light-blue--text text--darken-1"
        outlined
        to="/system-settings"
      >
        {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
      <v-btn
        v-if="userCanManage"
        :disabled="viewData===null || isSaving || !dataFormValid"
        :loading="isSaving"
        class="green white--text elevation-0"
        @click="onSaveWebsiteClick"
      >
        {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
    </v-card-actions>

    <ticket-request-dialog
      :message="ticketMessage"
      :ticket-model="ticketModel"
      :visible.sync="requestDialogVisible"
    />
  </v-card>
</template>

<script>
import VuetifyAudio from '@/components/common/sound/VuetifyAudio'
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import ReservationStatus from '@/mixins/reservation/reservationStatus'
import {clone} from '@/lib/helper/helper'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum'
import OrderTypeTime from '@/mixins/orders/orderTypeTime'
import SourceType from '@/mixins/orders/sourceType'
import OrderTypeEnum from '@/api/Enums/OrderTypeEnum'
import OrderType from '@/mixins/orders/orderType'
import SupportTicketAddModel from '@/api/Models/support-center/SupportTicketAddModel'
import TicketRequestDialog from '@/components/support-center/TicketRequestDialog.vue'
import SupportDepartmentEnum from '@/api/Enums/SupportDepartmentEnum'
import SupportPriorityEnum from '@/api/Enums/SupportPriorityEnum'
import LangSearchHighlightMixin from '@/lang/highlight/langSearchHighlightMixin.js'
import LangSearchHighlight from '@/lang/highlight/LangSearchHighlight.vue'
import NotificationEditableLangValue from '@/components/settings/notifications/NotificationEditableLangValue.vue'

export default {
  components: {
    NotificationEditableLangValue,
    LangSearchHighlight,
    TicketRequestDialog,
    VuetifyAudio
  },
  mixins: [AppData, Auth, SourceType, ReservationStatus, OrderType, OrderTypeTime, LangSearchHighlightMixin],
  data() {
    return {
      isSaving       : false,
      dataFormValid  : false,
      validationRules: {
        dataForm: {
          Email: [
            (v) => v && (v.length >= 0 && v.length <= 96) || this.$t('Customer.CustomerAddEditDialog.Field.Info.Email.Error.Between'),
            (v) => v && (/^\w+([.\-\\+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(v)) || this.$t('Customer.CustomerAddEditDialog.Field.Info.Email.Error.Valid'),
            (v) => v && !this.emailIsDuplicate(v) || this.$t('Customer.CustomerAddEditDialog.Field.Info.Email.Error.Duplicate')
          ]
        }
      },
      selectedSound: {
        id  : -1,
        name: '',
        url : ''
      },
      ticketMessage       : '',
      requestDialogVisible: false,
      ticketModel         : new SupportTicketAddModel(),
      viewData            : null
    }
  },
  computed: {
    OrderTypeEnum() {
      return OrderTypeEnum
    },
    OrderStatusEnum() {
      return OrderStatusEnum
    },

    orderStatuses() {
      return this.$t('Order.OrdersAll.Filter.filterOrderStatus.Items')
    },

    orderStatusesFiltered() {
      const defaultStatuses = this.$t('Order.OrdersAll.Filter.filterOrderStatus.Items').filter(o => [OrderStatusEnum.Accepted, OrderStatusEnum.Preparation, OrderStatusEnum.Delivery, OrderStatusEnum.Completed, OrderStatusEnum.Canceled].includes(o.Id))
      //const defaultStatuses = this.$t('Order.OrdersAll.Filter.filterOrderStatus.Items').filter(o => [OrderStatusEnum.Accepted, OrderStatusEnum.Completed, OrderStatusEnum.Canceled].includes(o.Id))
      const allStatus = [this.$t('Order.OrdersAll.Filter.filterOrderStatus.ItemAll')]

      return [...allStatus, ...defaultStatuses]
    },

    orderTypesListFiltered() {
      return this.$t('Restaurant.CatalogAddDialog.Field.OrderType.Items')
    },

    sounds() {
      const arr = []
      let fileId = 1
      const totalSounds = 22

      for (fileId = 1; fileId <= totalSounds; fileId++) {
        arr.push(
          {
            id  : fileId,
            name: `${this.$t('Settings.Notification.Data.Sound.Field.Sound')} ${fileId}`,
            url : `${this.appConfig.LOCATION_DATA.CdnUrl}assets/sound/sound_${fileId}.mp3`
          }
        )
      }

      arr.unshift(
        {
          id  : 0,
          name: this.$t('Settings.Notification.Data.Sound.Field.NoSound'),
          url : '0'
        }
      )

      return arr
    }
  },
  watch: {},
  created() {
    // console.log('1. created');
  },
  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.Settings.Notifications.Get, this.onGetNotificationsResult)
    this.$bus.$on(window.SocketCommand.Settings.Notifications.Save, this.onSaveNotificationsResult)
    this.getData()
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Settings.Notifications.Get, this.onGetNotificationsResult)
    this.$bus.$off(window.SocketCommand.Settings.Notifications.Save, this.onSaveNotificationsResult)
  },
  methods: {
    clone,

    emailIsDuplicate(email) {
      return this.viewData.Emails.filter(e => String(e.Email).toLowerCase() === String(email).toLowerCase()).length > 1
    },

    isReservationActiveDisabled(reservation) {
      return !reservation.to?.length || !reservation.channel?.length
    },
    onReservationChange(reservation) {
      if (!reservation.to?.length || !reservation.channel?.length) reservation.active = false
    },

    getOrderStatusById(orderStatusId) {
      return this.orderStatuses.find(o => o.Id === orderStatusId)
    },

    getData() {
      window.callAS(window.SocketCommand.Settings.Notifications.Get, {})
    },

    onGetNotificationsResult(result) {
      const activeSound = this.sounds.find(s => s.url.includes(result.Sound))
      if (activeSound) this.selectedSound = activeSound

      result.Reservation.forEach(reservation => this.onReservationChange(reservation))

      if (!result.Notify) result.Notify = []
      if (!result.NotifyManual) result.NotifyManual = []
      if (!result.DeliveryNotify) result.DeliveryNotify = []

      this.viewData = result
    },

    getNotificationMinutes(minutes) {
      return minutes > 0 ? minutes : 'X'
    },

    onDeliveryNotificationMessageSave(data) {
      this.viewData.DeliveryNotify[data.Index].message[data.Type] = data.Value
    },

    addDeliveryNotifySource() {
      const payload = {
        active : true,
        source : 0,
        status : 0,
        type   : 0,
        message: {
          email: {
            subject: {
              el: this.$t('Settings.Notification.Data.Delivery.Message.Email.Subject', 'el'),
              en: this.$t('Settings.Notification.Data.Delivery.Message.Email.Body', 'en')
            },
            body: {
              el: this.$t('Settings.Notification.Data.Delivery.Message.Email.Subject', 'el'),
              en: this.$t('Settings.Notification.Data.Delivery.Message.Email.Body', 'en')
            }
          },
          sms: {
            el: this.$t('Settings.Notification.Data.Delivery.Message.Sms', 'el'),
            en: this.$t('Settings.Notification.Data.Delivery.Message.Sms', 'en')
          },
          push: {
            el: this.$t('Settings.Notification.Data.Delivery.Message.Push', 'el'),
            en: this.$t('Settings.Notification.Data.Delivery.Message.Push', 'en')
          }
        },
        via: {
          email: [],
          sms  : [],
          push : []
        }
      }

      this.viewData.DeliveryNotify.push(payload)
    },

    addNotifyManualSource() {
      const payload = {
        active: true,
        source: 0,
        status: 0,
        type  : 0,
        via   : {
          email: [],
          sms  : [],
          push : []
        }
      }

      this.viewData.NotifyManual.push(payload)
    },

    addNotifySource() {
      const payload = {
        active: true,
        source: 0,
        status: 0,
        type  : 0,
        via   : {
          email: [],
          sms  : [],
          push : []
        }
      }

      this.viewData.Notify.push(payload)
    },

    addNotifyEmail() {
      const payload = {
        Type    : [0],
        Email   : '',
        Label   : '',
        Priority: 0,
        Notes   : '',
        Active  : true
      }

      this.viewData.Emails.push(payload)
    },
    removeNotifyEmail(index) {
      this.viewData.Emails.splice(index, 1)
    },
    changeNotifyEmailType(index) {
      const item = this.viewData.Emails[index]
      if (item.Type[0] === 0 && item.Type.length > 1) item.Type.splice(0, 1)
      if (item.Type[item.Type.length - 1] === 0 && item.Type.length > 1) item.Type = [0]
    },

    removeDeliveryNotifySource(index) {
      this.viewData.DeliveryNotify.splice(index, 1)
    },

    removeNotifyManualSource(index) {
      this.viewData.NotifyManual.splice(index, 1)
    },

    removeNotifySource(index) {
      this.viewData.Notify.splice(index, 1)
    },

    onRequestDialogShow(type) {
      const CurrentPlanId = this.appConfig?.LOCATION_DATA?.PackageId

      const Subject = this.$t(`Settings.Notification.SupportRequest.${type}.Subject`)
      const Message = this.$t(`Settings.Notification.SupportRequest.${type}.Message`)
      const Department = SupportDepartmentEnum.SALES
      const Priority = SupportPriorityEnum.MEDIUM
      const Service = this.$t(`Account.Plan.Package.${CurrentPlanId}.ServiceId`)

      this.ticketMessage = Message
      this.ticketModel = new SupportTicketAddModel({
        subject   : Subject,
        department: Department,
        priority  : Priority,
        service   : Service
      })

      this.requestDialogVisible = true
    },

    onSaveWebsiteClick() {
      this.$refs.dataForm.validate()

      if (this.dataFormValid) {
        this.isSaving = true
        const payload = clone(this.viewData)
        const s = this.selectedSound.url.split('/')
        if (Array.isArray(s) && s.length > 0) payload.Sound = s[s.length - 1]

        payload.Reservation.forEach(reservation => this.onReservationChange(reservation))

        window.callAS(window.SocketCommand.Settings.Notifications.Save, {Notification: payload})
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.FieldError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    onSaveNotificationsResult(result) {
      this.isSaving = false

      if (result.status) {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: result.type,
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Notification.Notification'),
          type: 'error',
          icon: 'warning'
        })
      }
    }
  }
}
</script>

<style scoped>

</style>
